import React, { useMemo, useState } from 'react';

interface SnoozePickerProps {
  onConfirm: (untilIso: string, rationale: string | undefined) => Promise<void> | void;
  onCancel?: () => void;
  /** When true, render Submit as a busy/disabled state during in-flight POST. */
  busy?: boolean;
}

type Preset = '24h' | '7d' | '30d' | 'custom';

function computeUntilIso(preset: Preset, customDateTime: string): string | null {
  const now = Date.now();
  if (preset === '24h') return new Date(now + 24 * 3600 * 1000).toISOString();
  if (preset === '7d') return new Date(now + 7 * 24 * 3600 * 1000).toISOString();
  if (preset === '30d') return new Date(now + 30 * 24 * 3600 * 1000).toISOString();
  if (preset === 'custom') {
    if (!customDateTime) return null;
    // datetime-local input is parsed as local TZ; toISOString normalizes to UTC.
    const d = new Date(customDateTime);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }
  return null;
}

/**
 * Hybrid snooze duration picker per parked decision #2.
 * WQ-283 (DEF-023): Submit is wired to the snooze endpoint via the
 * onConfirm callback supplied by the parent (ThreatDetailPanel).
 */
export function SnoozePicker({ onConfirm, onCancel, busy }: SnoozePickerProps): React.JSX.Element {
  const [preset, setPreset] = useState<Preset>('24h');
  const [customDateTime, setCustomDateTime] = useState('');
  const [rationale, setRationale] = useState('');

  const untilIso = useMemo(() => computeUntilIso(preset, customDateTime), [preset, customDateTime]);
  const submitDisabled = busy || !untilIso;

  const handleSubmit = async () => {
    if (!untilIso) return;
    await onConfirm(untilIso, rationale.trim() ? rationale.trim() : undefined);
  };

  return (
    <div
      role="group"
      aria-label="Snooze duration"
      style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
    >
      <div style={{ fontWeight: 600 }}>Snooze for:</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="radio"
          name="snooze-duration"
          checked={preset === '24h'}
          onChange={() => setPreset('24h')}
        />
        24 hours
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="radio"
          name="snooze-duration"
          checked={preset === '7d'}
          onChange={() => setPreset('7d')}
        />
        7 days
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="radio"
          name="snooze-duration"
          checked={preset === '30d'}
          onChange={() => setPreset('30d')}
        />
        30 days
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="radio"
          name="snooze-duration"
          checked={preset === 'custom'}
          onChange={() => setPreset('custom')}
        />
        Custom:
        <input
          type="datetime-local"
          value={customDateTime}
          onChange={(e) => setCustomDateTime(e.target.value)}
          disabled={preset !== 'custom'}
          aria-label="Custom snooze date and time"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.875rem' }}>
        Rationale (optional):
        <input
          type="text"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          maxLength={2000}
          placeholder="Why snooze this event?"
        />
      </label>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitDisabled}
          aria-disabled={submitDisabled}
          aria-label="Snooze"
        >
          {busy ? 'Submitting…' : 'Snooze'}
        </button>
      </div>
    </div>
  );
}
